import React, { useEffect, useState } from "react";
import {
    Paper,
    Typography,
    Box,
    Divider,
    TextField,
    Button,
    Chip,
    Stack,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { rtdb } from "../../services/firebase";
import { ref, get, set } from "firebase/database";


export default function Settings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Frete
    const [frete, setFrete] = useState("");

    // Filtros
    const [inputCategoria, setInputCategoria] = useState("");
    const [categorias, setCategorias] = useState([]);

    const [inputMarca, setInputMarca] = useState("");
    const [marcas, setMarcas] = useState([]);

    // Produtos
    const [produtos, setProdutos] = useState([]);
    const [searchProduto, setSearchProduto] = useState("");
    const [maisVendidos, setMaisVendidos] = useState([]);

    // Toast
    const [toastOpen, setToastOpen] = useState(false);

    useEffect(() => {
        carregarDados();
    }, []);

    async function carregarDados() {
        try {
            // Load configs
            const appSnap = await get(ref(rtdb, "app"));
            if (appSnap.exists()) {
                const data = appSnap.val();

                setFrete(data.frete || "");
                setCategorias(data.category || []);
                setMarcas(data.brands || []);
                setMaisVendidos(data.topSales || []);
            }

            // Load products
            const prodSnap = await get(ref(rtdb, "products"));
            if (prodSnap.exists()) {
                const raw = prodSnap.val();
                const arr = Object.keys(raw).map((id) => ({
                    id,
                    ...raw[id],
                }));
                setProdutos(arr);
            }
        } catch (err) {
            console.log("Erro ao carregar configs:", err);
        } finally {
            setLoading(false);
        }
    }

    async function salvar() {
        setSaving(true);

        try {
            // Buscar dados atuais para garantir que não perdemos nada
            const appSnap = await get(ref(rtdb, "app"));
            const dadosAtuais = appSnap.exists() ? appSnap.val() : {};

            await set(ref(rtdb, "app"), {
                ...dadosAtuais,
                ship: frete || null,
                category: categorias,
                brands: marcas,
                topSales: maisVendidos,
            });

            setToastOpen(true);
        } catch (err) {
            console.log("Erro ao salvar:", err);
        } finally {
            setSaving(false);
        }
    }


    async function adicionarCategoria() {
        const novaCategoria = inputCategoria.trim();
        if (novaCategoria !== "" && !categorias.includes(novaCategoria)) {
            const novasCategorias = [...categorias, novaCategoria];
            setCategorias(novasCategorias);
            
            // Salvar automaticamente no Firebase
            try {
                const appSnap = await get(ref(rtdb, "app"));
                const dadosAtuais = appSnap.exists() ? appSnap.val() : {};
                
                await set(ref(rtdb, "app"), {
                    ...dadosAtuais,
                    category: novasCategorias,
                });
            } catch (err) {
                console.error("Erro ao salvar categoria:", err);
                // Reverter em caso de erro
                setCategorias(categorias);
            }
        }
        setInputCategoria("");
    }

    async function adicionarMarca() {
        const novaMarca = inputMarca.trim();
        if (novaMarca !== "" && !marcas.includes(novaMarca)) {
            const novasMarcas = [...marcas, novaMarca];
            setMarcas(novasMarcas);
            
            // Salvar automaticamente no Firebase
            try {
                const appSnap = await get(ref(rtdb, "app"));
                const dadosAtuais = appSnap.exists() ? appSnap.val() : {};
                
                await set(ref(rtdb, "app"), {
                    ...dadosAtuais,
                    brands: novasMarcas,
                });
            } catch (err) {
                console.error("Erro ao salvar marca:", err);
                // Reverter em caso de erro
                setMarcas(marcas);
            }
        }
        setInputMarca("");
    }

    async function removerCategoria(categoriaRemover) {
        const novasCategorias = categorias.filter((c) => c !== categoriaRemover);
        setCategorias(novasCategorias);
        
        // Salvar automaticamente no Firebase
        try {
            const appSnap = await get(ref(rtdb, "app"));
            const dadosAtuais = appSnap.exists() ? appSnap.val() : {};
            
            await set(ref(rtdb, "app"), {
                ...dadosAtuais,
                category: novasCategorias,
            });
        } catch (err) {
            console.error("Erro ao remover categoria:", err);
            // Reverter em caso de erro
            setCategorias(categorias);
        }
    }

    async function removerMarca(marcaRemover) {
        const novasMarcas = marcas.filter((m) => m !== marcaRemover);
        setMarcas(novasMarcas);
        
        // Salvar automaticamente no Firebase
        try {
            const appSnap = await get(ref(rtdb, "app"));
            const dadosAtuais = appSnap.exists() ? appSnap.val() : {};
            
            await set(ref(rtdb, "app"), {
                ...dadosAtuais,
                brands: novasMarcas,
            });
        } catch (err) {
            console.error("Erro ao remover marca:", err);
            // Reverter em caso de erro
            setMarcas(marcas);
        }
    }

    const produtosFiltrados = produtos.filter((p) =>
        p.nome?.toLowerCase().includes(searchProduto.toLowerCase())
    );


    if (loading)
        return (
            <Box sx={{ ml: 32, p: 5 }}>
                <CircularProgress />
            </Box>
        );

    return (
        <Box sx={{ ml: 32, p: 4, pb: 8 }}>

            {/* Título da página */}
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Configurações gerais da Loja
            </Typography>
            <Typography sx={{ mt: 1, mb: 4, color: "#555" }}>
                Ajuste informações que afetam diretamente o site público,
                como frete, filtros de navegação e produtos destacados da Home.
            </Typography>

            {/* ================= FRETE ================= */}
            <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Frete
                </Typography>
                <Typography sx={{ mb: 2, color: "#666" }}>
                    Defina aqui o valor fixo cobrado no checkout. Esse valor será aplicado
                    a todos os pedidos, exceto quando houver regras especiais.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <TextField
                    label="Valor do frete (R$)"
                    placeholder="Ex: 12.90"
                    type="number"
                    sx={{
                        width: 300,
                        "& .MuiInputLabel-root": { color: "#777", fontWeight: 500 },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#0A2A79" },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#0A2A79" }
                    }}
                    value={frete}
                    onChange={(e) => setFrete(e.target.value)}
                />
            </Paper>

            {/* ================= FILTROS ================= */}
            <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Filtros de Navegação
                </Typography>
                <Typography sx={{ mb: 2, color: "#666" }}>
                    Esses filtros aparecerão na página de produtos e ajudam o cliente
                    a encontrar o que procura com facilidade.
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* CATEGORIAS */}
                <Typography sx={{ fontWeight: 600 }}>Categorias</Typography>
                <Typography sx={{ mb: 1, color: "#777" }}>
                    Use nomes curtos. Exemplo: Pods, Acessórios, Ignite.
                </Typography>

                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Nova categoria"
                        value={inputCategoria}
                        onChange={(e) => setInputCategoria(e.target.value)}
                        sx={{
                            width: 300,
                            "& .MuiInputLabel-root": { color: "#777", fontWeight: 500 },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#0A2A79" },
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#0A2A79" }
                        }}
                    />
                    <Button variant="contained" onClick={adicionarCategoria}>
                        Adicionar
                    </Button>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1.5, mb: 3 }}>
                    {categorias.map((cat) => (
                        <Chip
                            key={cat}
                            label={cat}
                            onDelete={() => removerCategoria(cat)}
                            sx={{ mb: 1, fontSize: 15, p: "4px 8px" }}
                        />
                    ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* MARCAS */}
                <Typography sx={{ fontWeight: 600 }}>Marcas</Typography>
                <Typography sx={{ mb: 1, color: "#777" }}>
                    Essas marcas aparecerão no filtro lateral da página Produtos.
                </Typography>

                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Nova marca"
                        value={inputMarca}
                        onChange={(e) => setInputMarca(e.target.value)}
                        sx={{
                            width: 300,
                            "& .MuiInputLabel-root": { color: "#777", fontWeight: 500 },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#0A2A79" },
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#0A2A79" }
                        }}
                    />
                    <Button variant="contained" onClick={adicionarMarca}>
                        Adicionar
                    </Button>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {marcas.map((marca) => (
                        <Chip
                            key={marca}
                            label={marca}
                            onDelete={() => removerMarca(marca)}
                            sx={{ mb: 1, fontSize: 15, p: "4px 8px" }}
                        />
                    ))}
                </Stack>
            </Paper>

            {/* ================= MAIS VENDIDOS ================= */}
            <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Produtos em Destaque na Home
                </Typography>

                <Typography sx={{ mb: 2, color: "#666" }}>
                    Escolha até 15 produtos para aparecerem na seção "Mais Vendidos".
                    Essa é uma área estratégica de destaque na página inicial.
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Campo de busca */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <SearchIcon sx={{ mr: 1 }} />
                    <TextField
                        label="Buscar produto..."
                        fullWidth
                        value={searchProduto}
                        onChange={(e) => setSearchProduto(e.target.value)}
                        sx={{
                            "& .MuiInputLabel-root": { color: "#777", fontWeight: 500 },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#0A2A79" },
                            "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#0A2A79" }
                        }}
                    />
                </Box>

                {/* Lista de produtos */}
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                    {maisVendidos.length} / 15 selecionados
                </Typography>

                <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                    {produtosFiltrados.map((produto) => {
                        const selecionado = maisVendidos.includes(produto.id);

                        return (
                            <Box
                                key={produto.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    py: 1.5,
                                    mb: 1,
                                    borderBottom: "1px solid #eee",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img
                                        src={produto.imagens?.[0]}
                                        alt=""
                                        style={{
                                            width: 45,
                                            height: 45,
                                            borderRadius: 6,
                                            marginRight: 12,
                                            objectFit: "cover",
                                        }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {produto.nome}
                                        </Typography>
                                        <Typography sx={{ color: "#777", fontSize: 13 }}>
                                            {produto.marca} • {produto.categoria} • {produto.precos.normal}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Button
                                    variant={selecionado ? "outlined" : "contained"}
                                    color={selecionado ? "error" : "primary"}
                                    onClick={() => {
                                        if (selecionado) {
                                            setMaisVendidos(
                                                maisVendidos.filter((id) => id !== produto.id)
                                            );
                                        } else if (maisVendidos.length < 15) {
                                            setMaisVendidos([...maisVendidos, produto.id]);
                                        }
                                    }}
                                >
                                    {selecionado ? "Remover" : "Adicionar"}
                                </Button>
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            {/* BOTÃO SALVAR */}
            <Box sx={{ textAlign: "right", mt: 4 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={salvar}
                    disabled={saving}
                >
                    {saving ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                        "Salvar Configurações"
                    )}
                </Button>
            </Box>

            {/* Toast */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
            >
                <Alert severity="success">Configurações salvas com sucesso!</Alert>
            </Snackbar>
        </Box>
    );

}
